import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NewsArticle } from '../../../mocks/news';
import { categoryBadgeClass } from '../../../services/news';
import { useTranslation } from 'react-i18next';

interface NewsGridProps {
  articles: NewsArticle[];
}

const PAGE_SIZE = 6;

export default function NewsGrid({ articles }: NewsGridProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(articles.length / PAGE_SIZE);
  const displayed = articles.slice(0, page * PAGE_SIZE);
  const hasMore = page < totalPages;

  if (articles.length === 0) {
    return (
      <section className="bg-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-light-alt border border-primary/10">
            <i className="ri-file-search-line text-gray-300 text-2xl" />
          </div>
          <p className="text-gray-400 text-base">{t('newsPage.grid.empty')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white pb-24">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-center gap-2 mb-8">
          <span className="w-1 h-4 rounded-full bg-accent" />
          <span className="text-xs text-gray-400 uppercase tracking-widest font-medium">{t('newsPage.grid.total', { count: articles.length })}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayed.map((article, idx) => (
            <Link
              key={article.id}
              to={`/news/${article.id}`}
              className="group bg-light-alt border border-primary/10 rounded-2xl overflow-hidden hover:border-primary/25 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className="relative w-full h-48 overflow-hidden">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-light-alt/80 via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full border ${categoryBadgeClass(article.category)}`}>
                    {article.categoryLabel}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-4 text-gray-400 text-xs mb-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line text-xs" /></div>
                    {article.date}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line text-xs" /></div>
                    {article.readTime}
                  </div>
                </div>

                <h3 className="text-base font-bold text-gray-800 leading-snug mb-3 group-hover:text-primary transition-colors duration-200 line-clamp-2">
                  {article.title}
                </h3>

                <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">{article.summary}</p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {article.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-white border border-primary/10 text-gray-400 text-xs rounded">{tag}</span>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary/10 border border-primary/15">
                      <i className="ri-user-line text-primary text-xs" />
                    </div>
                    {article.author}
                  </div>
                  <div className="flex items-center gap-1 text-primary text-xs font-medium group-hover:gap-2 transition-all duration-200">
                    {t('news.read')}
                    <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-line text-xs" /></div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-12">
            <button onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-2 px-8 py-3 bg-light-alt border border-primary/15 text-gray-500 hover:text-primary hover:border-primary/30 text-sm font-medium rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line" /></div>
              {t('newsPage.grid.loadMore', { remaining: articles.length - displayed.length })}
            </button>
          </div>
        )}
        {!hasMore && articles.length > PAGE_SIZE && (
          <div className="flex justify-center mt-12">
            <span className="text-gray-400 text-sm"><i className="ri-check-line mr-2 text-primary" />{t('newsPage.grid.allShown', { count: articles.length })}</span>
          </div>
        )}
      </div>
    </section>
  );
}
